// Small shared UI pieces (Tailwind, in the style of shadcn/ui). Colours come from the semantic tokens in styles.css
// (bg-surface, text-muted, border-line, bg-accent…) so every piece works in light and dark mode.
import { useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";

export function cx(...classes: (string | false | null | undefined)[]) {
  return classes.filter(Boolean).join(" ");
}

/** 980 → "980 B", 20480 → "20 KB", 5_400_000 → "5.1 MB", 2^30 → "1 GB". */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Number((bytes / 1024 ** 3).toFixed(1))} GB`;
}

/** Keyboard focus ring shared by buttons, links and chips. */
export const focusRing = "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent";

type Variant = "primary" | "secondary" | "danger" | "ghost";
type Size = "sm" | "md";
const variants: Record<Variant, string> = {
  primary: "bg-accent text-on-accent shadow-xs hover:bg-accent-hover disabled:opacity-50 disabled:hover:bg-accent",
  secondary: "bg-surface text-fg shadow-xs ring-1 ring-inset ring-line-strong hover:bg-subtle disabled:text-faint disabled:hover:bg-surface",
  danger: "bg-danger text-white shadow-xs hover:bg-danger-hover disabled:opacity-50 disabled:hover:bg-danger",
  ghost: "text-muted hover:bg-subtle hover:text-fg disabled:text-faint disabled:hover:bg-transparent",
};
const sizes: Record<Size, string> = { sm: "h-8 px-3 text-xs", md: "h-9 px-4 text-sm" };

/** Button styles, also for links that act as buttons. */
export function buttonClass(variant: Variant = "primary", size: Size = "md") {
  return cx(
    "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg font-semibold transition-colors disabled:cursor-not-allowed",
    focusRing,
    variants[variant],
    sizes[size],
  );
}

export function Button({ variant = "primary", size = "md", className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size }) {
  return <button type="button" {...props} className={cx(buttonClass(variant, size), className)} />;
}

interface FieldProps {
  label: string;
  error?: string;
  hint?: ReactNode;
  children: (id: string, describedBy: string | undefined) => ReactNode;
}

export function Field({ label, error, hint, children }: FieldProps) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-fg">
        {label}
      </label>
      {children(id, describedBy)}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-sm text-bad">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs leading-relaxed text-faint">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

const inputClass = (invalid?: boolean) =>
  cx(
    "block w-full rounded-lg border bg-surface px-3 py-2 text-sm text-fg shadow-xs transition-colors placeholder:text-faint focus:outline-none focus:ring-3 disabled:cursor-not-allowed disabled:bg-subtle disabled:text-faint",
    invalid ? "border-bad focus:border-bad focus:ring-bad/20" : "border-line-strong focus:border-accent focus:ring-accent/20",
  );

export function TextInput({ invalid, ...props }: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return <input {...props} aria-invalid={invalid || undefined} className={cx(inputClass(invalid), props.className)} />;
}

export function TextArea({ invalid, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return <textarea {...props} aria-invalid={invalid || undefined} className={cx(inputClass(invalid), props.className)} />;
}

export function Select({ invalid, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
  return <select {...props} aria-invalid={invalid || undefined} className={cx(inputClass(invalid), "pr-8", props.className)} />;
}

export function Card({ children, className, highlight, id }: { children: ReactNode; className?: string; highlight?: boolean; id?: string }) {
  return (
    <div id={id} className={cx("rounded-xl border p-5 shadow-xs sm:p-6", highlight ? "border-warn-line bg-warn-bg" : "border-line bg-surface", className)}>{children}</div>
  );
}

/** Heading inside a Card, with an optional line of explanation under it. */
export function CardTitle({ children, description }: { children: ReactNode; description?: ReactNode }) {
  return (
    <>
      <h2 className="text-base font-semibold tracking-tight text-fg">{children}</h2>
      {description && <p className="text-sm text-muted">{description}</p>}
    </>
  );
}

/** Page title row: heading, optional description, and actions on the right. */
export function PageHeader({ title, description, actions }: { title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
      <div className="min-w-0 space-y-1">
        <h1 className="break-words text-2xl font-semibold tracking-tight text-fg">{title}</h1>
        {description && <p className="text-sm text-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

/** Friendly placeholder for a list or page with nothing in it. */
export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-line-strong bg-surface/60 px-6 py-12 text-center">
      {icon && <div className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-soft text-accent-fg [&>svg]:h-5 [&>svg]:w-5">{icon}</div>}
      {title}
      {children && <div className="max-w-md text-sm text-muted">{children}</div>}
      {action}
    </div>
  );
}

type Tone = "neutral" | "good" | "warn" | "bad" | "info";
const badgeTones: Record<Tone, string> = {
  neutral: "bg-subtle text-muted ring-line-strong",
  good: "bg-good-bg text-good-fg ring-good-line",
  warn: "bg-warn-bg text-warn-fg ring-warn-line",
  bad: "bg-bad-bg text-bad-fg ring-bad-line",
  info: "bg-info-bg text-info-fg ring-info-line",
};

export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return <span className={cx("inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset", badgeTones[tone])}>{children}</span>;
}

const alertTones: Record<Tone, string> = {
  neutral: "border-line bg-subtle text-fg",
  good: "border-good-line bg-good-bg text-good-fg",
  warn: "border-warn-line bg-warn-bg text-warn-fg",
  bad: "border-bad-line bg-bad-bg text-bad-fg",
  info: "border-info-line bg-info-bg text-info-fg",
};

const alertIcons: Record<Tone, string> = {
  neutral: "M12 16v-4m0-4h.01M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z",
  info: "M12 16v-4m0-4h.01M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z",
  good: "m9 12 2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
  warn: "M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z",
  bad: "M12 8v4m0 4h.01M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z",
};

export function Alert({ tone = "info", children, onDismiss }: { tone?: Tone; children: ReactNode; onDismiss?: () => void }) {
  return (
    <div role={tone === "bad" ? "alert" : "status"} className={cx("flex items-start gap-3 rounded-lg border px-4 py-3 text-sm leading-relaxed", alertTones[tone])}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 h-4 w-4 shrink-0 opacity-80" aria-hidden>
        <path d={alertIcons[tone]} />
      </svg>
      <div className="min-w-0 flex-1">{children}</div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} className={cx("-m-1 rounded-md p-1 opacity-70 transition-opacity hover:opacity-100", focusRing)} aria-label="Dismiss">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-4 w-4" aria-hidden>
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

/** A button placed inside an Alert's text, e.g. "Reload" or "Install". */
export function AlertAction(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <Button variant="secondary" size="sm" {...props} className={cx("ml-2 align-middle", props.className)} />;
}

export function Dialog({ open, title, children, onClose }: { open: boolean; title: string; children: ReactNode; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal?.();
    if (!open && el.open) el.close?.();
  }, [open]);
  if (!open) return null;
  return (
    <dialog
      ref={ref}
      open={typeof HTMLDialogElement === "undefined" || !("showModal" in HTMLDialogElement.prototype) ? true : undefined}
      onClose={onClose}
      onCancel={onClose}
      aria-label={title}
      className="m-auto w-[min(92vw,28rem)] rounded-2xl border border-line bg-surface p-0 text-fg shadow-2xl backdrop:bg-black/50 backdrop:backdrop-blur-[2px]"
    >
      <div className="space-y-4 p-6">
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {children}
      </div>
    </dialog>
  );
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div role="status" className="flex items-center gap-2 text-sm text-muted">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-line-strong border-t-accent" aria-hidden />
      {label}
    </div>
  );
}
